import { RecommendationDto } from '@shared/models';
import { ChangeDetectionStrategy, Component, inject, input } from '@angular/core';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { ButtonV3Component, ComplexDialogV3Service, SnackbarModule } from '@gea/digital-ui-lib';

import { TroubleshootingStore } from '@shared/state';
import { BRCBaseComponent } from '@brc/hrt-base-component';

@Component({
  selector: 'gea-hrt-recommendation-editable-actions',
  styleUrl: './actions.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [ButtonV3Component, TranslatePipe, SnackbarModule],
  template: `
    <div class="editable-actions">
      @if (recommendation().reviewStatus === 'useful') {
        <geaui-button-v3
          type="secondary"
          data-testid="btn-save"
          preIcon="folder"
          (click)="updateRecommendation(recommendation())">
          <span>
            {{ 'TROUBLESHOOTING.RECOMMENDATIONS.BUTTON_SAVE' | translate }}
          </span>
        </geaui-button-v3>
      }

      <geaui-button-v3
        information="error"
        [inverted]="true"
        data-testid="btn-delete"
        class="delete-button"
        preIcon="remove"
        (click)="openDeleteRecommendationDialog(recommendation())">
        {{ 'UI-LIB.GENERAL.DELETE' | translate }}
      </geaui-button-v3>

      <geaui-button-v3
        type="primary"
        data-testid="btn-edit"
        preIcon="pen-01"
        (click)="openEditRecommendationDialog(recommendation())">
        {{ 'TROUBLESHOOTING.RECOMMENDATIONS.BUTTON_EDIT' | translate }}
      </geaui-button-v3>
    </div>
    <geaui-snackbar />
  `,
})
export class EditableRecommendationActionsComponent extends BRCBaseComponent {
  readonly recommendation = input.required<RecommendationDto>();
  readonly translate = inject(TranslateService);
  private readonly _complexDialogService = inject(ComplexDialogV3Service);
  private readonly troubleshootingStore = inject(TroubleshootingStore);

  updateRecommendation(recommendation: RecommendationDto) {
    this.troubleshootingStore.updateRecommendation(recommendation).subscribe({
      next: () => this.showSuccess('TROUBLESHOOTING.RECOMMENDATIONS.UPDATE_SUCCESS'),
      error: () => this.showError('ERROR.UPDATE_RECOMMENDATION_FAILED'),
    });
  }

  async openDeleteRecommendationDialog(recommendation: RecommendationDto) {
    const { DeleteRecommendationDialogComponent } = await import('@tab-container/fault-recommendations');

    this._complexDialogService.open(
      {
        header: 'DELETE_RECOMMENDATION.TITLE',
        closable: true,
        no: 'UI-LIB.GENERAL.CANCEL',
        yes: 'UI-LIB.GENERAL.DELETE',
        data: { recommendation },
        confirmCallback: () => this.confirmDeleteRecommendation(recommendation),
      },
      DeleteRecommendationDialogComponent
    );
  }

  async openEditRecommendationDialog(recommendation: RecommendationDto) {
    const { RecommendationDialogComponent } = await import('@tab-container/fault-recommendations');

    this._complexDialogService.open(
      {
        header: 'EDIT_RECOMMENDATION.DIALOG.TITLE',
        closable: true,
        hideButtons: true,
        data: { recommendation },
        confirmCallback: (output) => {
          const result = output as RecommendationDto | undefined;
          if (result?.actionName) {
            this.updateRecommendation({ ...recommendation, ...result });
          }
        },
      },
      RecommendationDialogComponent
    );
  }

  confirmDeleteRecommendation({ id }: RecommendationDto) {
    this.troubleshootingStore.deleteRecommendation(id).subscribe({
      next: () => this.showSuccess('RECOMMENDATION_DELETED_SUCCESSFULLY'),
      error: () => this.showError('TROUBLESHOOTING.RECOMMENDATIONS.DELETE_FAIL'),
    });
  }
}
